// Apolo IDE — S5 · subagent run store (session-keyed, survives navigation).
//
// Fonte de dados do SubagentsRailSection para os subagents S5 (explore/bash/
// browser) spawnados NESTA sessão. Mesma ideia do agentRunStore: módulo
// singleton (sem React) que sobrevive a sair/voltar da tela; a rail subscreve.
// Só o `summary` (já isolado pelo runtime) entra aqui — nunca rawOutput.

import { runSubagent } from './subagent.js';

const store = new Map(); // sessionId -> { entries: Entry[], subs: Set }
const MAX_ENTRIES = 50;
let liveSeq = 0;

const EMPTY = Object.freeze([]);

function ensure(id) {
  let r = store.get(id);
  if (!r) { r = { entries: [], subs: new Set() }; store.set(id, r); }
  return r;
}

function emit(r) {
  for (const cb of r.subs) cb(r.entries);
}

function upsert(sessionId, entry) {
  if (!sessionId) return;
  const r = ensure(sessionId);
  const i = r.entries.findIndex((e) => e.id === entry.id);
  if (i >= 0) r.entries = r.entries.map((e, j) => (j === i ? { ...e, ...entry } : e));
  else r.entries = [...r.entries, entry].slice(-MAX_ENTRIES);
  emit(r);
}

/** A subagent começou (status 'running'). */
export function publishSubagentStart(sessionId, { id, kind, input, parentTaskId }) {
  upsert(sessionId, { id, kind, input: String(input || ''), parentTaskId, status: 'running', summary: '' });
}

/** A subagent terminou — só o SubagentRun isolado (status 'completed'/'failed'). */
export function publishSubagentDone(sessionId, run, { blocked = false } = {}) {
  upsert(sessionId, {
    id: run.id, kind: run.kind, input: run.input, parentTaskId: run.parentTaskId,
    summary: run.summary, artifacts: run.artifacts,
    status: blocked ? 'cancelled' : 'completed',
  });
}

export function publishSubagentError(sessionId, id, message) {
  upsert(sessionId, { id, status: 'failed', summary: String(message || 'erro') });
}

export function getSubagentRuns(sessionId) {
  const r = store.get(sessionId);
  return r ? r.entries : EMPTY;
}

export function subscribeSubagentRuns(sessionId, cb) {
  const r = ensure(sessionId);
  r.subs.add(cb);
  return () => { r.subs.delete(cb); };
}

export function __resetSubagentRuns() {
  store.clear();
  liveSeq = 0;
}

/**
 * Runner instrumentado: roda um subagent E publica seu ciclo de vida no store
 * (running → completed/failed/cancelled), pra rail mostrar status ao vivo. Só o
 * summary isolado é publicado. Erros de profundidade/kind viram entry 'failed'.
 *
 * @param {string} sessionId
 * @param {Object} opts  opts do runSubagent
 * @param {Function} [run] injetável (default runSubagent)
 */
export async function runTrackedSubagent(sessionId, opts, run = runSubagent) {
  liveSeq += 1;
  const id = (opts && opts.id) || `sub_${opts && opts.kind}_live_${liveSeq}`;
  publishSubagentStart(sessionId, { id, kind: opts && opts.kind, input: opts && opts.input, parentTaskId: opts && opts.parentTaskId });
  try {
    const result = await run({ ...opts, id });
    publishSubagentDone(sessionId, result, { blocked: Boolean(result && result.blocked) });
    return result;
  } catch (e) {
    publishSubagentError(sessionId, id, e && e.message ? e.message : String(e));
    throw e;
  }
}
